import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Botnet Command (level 65) — the Real-time rung.
//
// Note what is NOT in the props below: `busy`. The host sets it for every action including a poll, and
// this view polls two or three times a second, so anything gated on it would flicker in and out and drop
// the taps that land during a poll. Load Balancer's node buttons were unusable for exactly that, and
// Rootkit Assembly's start button repeated it one rung later. Not destructuring it at all is the check
// that it cannot creep back in.
//
// Orders are STANDING, so nothing here is timed and no click can miss a window: an order that arrives
// late costs a fraction of a second of ramp-up and nothing else. Taps are queued and flushed one at a
// time rather than dropped while a request is in flight, because the session upsert carries no etag and
// two concurrent writes would lose one.
//
// The wall clock is anchored to `Date.now() - startedAgoMs`, never to when this component mounted: the
// session's clock belongs to the server, and a resumed attempt is already well into it.

interface NodeV {
  name: string;
  isTarget: boolean;
  floor: number;
  gripMs: number;
  hardness: number;
  integrity: number;
  share: number;
  defence: number;
  pressure: number;
  down: boolean;
}

interface SquadV {
  name: string;
  bandwidth: number;
  node: number;
  sinceMs: number;
  ramp: number;
}

interface Challenge {
  started: boolean;
  nowMs: number;
  startedAtMs: number;
  playMs: number;
  timeLimitSec: number;
  readingAllowanceSec: number;
  targetsNeeded: number;
  poolGrowthPerSec: number;
  repairPerSec: number;
  minCommitShare: number;
  reviveFraction: number;
  rampMs: number;
  poolBase: number;
  poolNow: number;
  fleetTotal: number;
  targetsDown: number;
  orders: number;
  squads: SquadV[];
  nodes: NodeV[];
}

const POLL_MS = 400;

export default function BotnetCommandView({
  challenge, onAct, onSubmit, setPendingSubmission, startedAgoMs,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [done, setDone] = useState(false);
  const [tick, setTick] = useState(0);
  const [queue, setQueue] = useState<{ squad: number; node: number }[]>([]);

  const sendingRef = useRef(false);
  const startedRef = useRef(challenge?.started ?? false);
  const doneRef = useRef(false);

  // The session's clock is the server's. Anchoring to mount time would leave a resumed attempt running
  // its own schedule from zero while the server had long moved on.
  const anchorRef = useRef<{ key: unknown; at: number }>({ key: challenge, at: Date.now() - startedAgoMs });
  if (anchorRef.current.key !== challenge) anchorRef.current = { key: challenge, at: Date.now() - startedAgoMs };

  useEffect(() => {
    setLive(challenge ?? null);
    setDone(false);
    setQueue([]);
    startedRef.current = challenge?.started ?? false;
    doneRef.current = false;
    sendingRef.current = false;
  }, [challenge]);

  useEffect(() => { setPendingSubmission({}); }, [setPendingSubmission]);

  const send = useCallback(async (action: unknown): Promise<boolean> => {
    if (sendingRef.current || doneRef.current) return false;
    sendingRef.current = true;
    try {
      const next = await onAct(action);
      if (next) { setLive(next); startedRef.current = next.started; return true; }
      // null means the attempt ended — the host owns the result screen from here.
      doneRef.current = true;
      setDone(true);
      return false;
    } finally {
      sendingRef.current = false;
    }
  }, [onAct]);

  // A ticker for the readouts that move between polls. Keyed on nothing that changes per poll, so it is
  // never torn down and rebuilt — the mistake that made Log Scrubber's stream appear to run at once.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 120);
    return () => clearInterval(id);
  }, []);

  // The grid advances on the server whether or not the player does anything, so it has to be asked. The
  // brief costs no requests at all: the gate is a ref, not a dependency, so the interval is not rebuilt
  // when the attempt starts.
  useEffect(() => {
    const id = setInterval(() => {
      if (!startedRef.current || doneRef.current || sendingRef.current) return;
      void send({ kind: 'poll' });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [send]);

  // Taps are queued, never dropped: one order at a time, in the order they were given.
  useEffect(() => {
    if (queue.length === 0 || done || sendingRef.current) return;
    const [head, ...rest] = queue;
    void (async () => {
      const ok = await send({ kind: 'order', squad: head.squad, node: head.node });
      setQueue(ok ? rest : []);
    })();
  }, [queue, tick, done, send]);

  const nodes = useMemo(() => live?.nodes ?? [], [live]);
  const squads = useMemo(() => live?.squads ?? [], [live]);
  const started = live?.started ?? false;

  // Where the pool is heading, worked out from the published rule and the published pressures. This is
  // arithmetic the player could do on paper, which is the line: showing them a sum they could compute is
  // not a leak, showing them a hidden fact would be.
  const heading = useMemo(() => {
    if (!live || nodes.length === 0) return [];
    const attacked = nodes.filter(n => n.pressure > 1e-9).length;
    if (attacked === 0) return nodes.map(n => n.share);
    const minEach = Math.min(live.minCommitShare, 1 / attacked);
    const rest = 1 - minEach * attacked;
    const total = nodes.reduce((a, n) => a + n.pressure, 0);
    return nodes.map(n => n.pressure > 1e-9 ? minEach + rest * (n.pressure / total) : 0);
  }, [live, nodes]);

  const order = (squad: number, node: number) => {
    if (done || squads[squad]?.node === node) return;
    setQueue(q => [...q.filter(x => x.squad !== squad), { squad, node }]);
  };

  const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const small: React.CSSProperties = { ...mono, fontSize: '0.72rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Five substations. <strong>Three of them are the target</strong> and two are worth nothing to
            you. The grid is taken when all three targets are <strong>dark at the same moment</strong> —
            not one after another.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            You command four squads. An order is a <strong>standing order</strong>: point a squad at a
            substation and it stays there until you move it, coming up to full output over about{' '}
            {((live?.rampMs ?? 1200) / 1000).toFixed(1)}s. Nothing here is timed to the millisecond — there
            is no window to miss.
          </p>
          <p style={{ ...mono, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The defence is one <strong>shared mitigation pool</strong>, and it moves. It commits at least{' '}
            <strong>{Math.round((live?.minCommitShare ?? 0.4) * 100)}%</strong> of itself to any substation
            under attack, however light the attack, and splits the rest by load. So you cannot out-muscle
            anything: put everything on one substation and the pool follows it there. What you can do is{' '}
            <strong>pull the pool somewhere it is not needed</strong> — a single squad on a worthless
            substation drags a slab of mitigation off the ones that matter. That is the whole game.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Each substation redeploys the pool at <strong>its own speed</strong>, which is stated. A
            slow-gripped one can be worn down by moving pressure around; a fast-gripped one has the pool
            back before you have done anything, and only yields when the pool has been drawn away. And a
            substation you leave alone <strong>repairs itself</strong> — {(
              (live?.repairPerSec ?? 0.012) * 100).toFixed(1)}% of its strength per second — and comes back
            online once it is {Math.round((live?.reviveFraction ?? 0.35) * 100)}% repaired. That is your
            real deadline.
          </p>
          <p style={{ ...mono, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            One more thing: the defender is bringing capacity online the whole time,{' '}
            {((live?.poolGrowthPerSec ?? 0.005) * 100).toFixed(1)}% of the pool per second. Every second
            you spend deciding makes the grid harder — so the clock is not a deadline so much as a price.
            The grid arrives when you ask for it; about {live?.readingAllowanceSec ?? 35}s of the limit is
            set aside for reading this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Bring up the grid
          </button>
        </div>
      </div>
    );
  }

  const poolNow = live?.poolNow ?? 0;
  const fleet = live?.fleetTotal ?? 0;
  const downCount = live?.targetsDown ?? 0;
  const needed = live?.targetsNeeded ?? 3;

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* Every readout has a fixed width. A number going 9 → 100 must not shove what sits next to it. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.55rem', background: '#0d1117', whiteSpace: 'nowrap',
      }}>
        <span style={{ ...mono, minWidth: '11rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>MITIGATION </span>
          <strong style={{ color: '#f87171' }}>{poolNow.toFixed(0)}</strong>
          <span style={{ color: '#4b5563' }}> / your {fleet.toFixed(0)} Gbps</span>
        </span>
        <span style={{ ...small, minWidth: '8rem', color: 'var(--text-muted)' }}>
          growing {((live?.poolGrowthPerSec ?? 0) * 100).toFixed(1)}%/s
        </span>
        <span style={{
          ...mono, minWidth: '7rem',
          color: downCount >= needed ? '#4ade80' : downCount > 0 ? '#fbbf24' : 'var(--text-muted)',
        }}>
          <strong>{downCount}</strong> of {needed} dark
        </span>
        <span style={{ ...small, minWidth: '5rem', color: '#4b5563' }}>
          {live?.orders ?? 0} orders
        </span>
      </div>

      <div style={{ display: 'grid', gap: '0.35rem', marginBottom: '0.55rem' }}>
        {nodes.map((n, i) => {
          const pct = Math.max(0, Math.min(100, (n.integrity / n.hardness) * 100));
          const surplus = n.pressure - n.defence;
          const to = heading[i] ?? n.share;
          const drift = to - n.share;

          return (
            <div key={n.name} style={{
              border: `1px solid ${n.down ? '#166534' : n.isTarget ? '#2b3444' : '#1c2029'}`,
              borderRadius: 4, padding: '0.4rem 0.55rem',
              background: n.isTarget ? '#0d1117' : '#0a0d12',
            }}>
              {/* Row one: what this substation IS. Never changes, so it never reflows. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                <span style={{
                  ...small, minWidth: '3.6rem',
                  color: n.isTarget ? '#f87171' : '#4b5563',
                  fontWeight: n.isTarget ? 700 : 400,
                }}>
                  {n.isTarget ? 'TARGET' : 'decoy'}
                </span>
                <strong style={{ ...mono, minWidth: '7.5rem', color: n.down ? '#4ade80' : '#e2e8f0' }}>
                  {n.name}
                </strong>
                <span style={{ ...small, minWidth: '9.5rem', color: 'var(--text-muted)' }}>
                  base {n.floor.toFixed(0)} · grip {(n.gripMs / 1000).toFixed(1)}s
                </span>
                <span style={{ ...small, minWidth: '7rem', color: '#9aa4b2' }}>
                  pool {Math.round(n.share * 100)}%
                  <span style={{ color: Math.abs(drift) < 0.02 ? '#4b5563' : drift > 0 ? '#f87171' : '#4ade80' }}>
                    {Math.abs(drift) < 0.02 ? ' =' : drift > 0 ? ` ↗${Math.round(to * 100)}` : ` ↘${Math.round(to * 100)}`}
                  </span>
                </span>
                <span style={{ ...small, minWidth: '11.5rem', color: '#9aa4b2' }}>
                  {n.pressure.toFixed(0)} vs {n.defence.toFixed(0)} ={' '}
                  <strong style={{ color: surplus > 0.5 ? '#4ade80' : surplus < -0.5 ? '#f87171' : '#fbbf24' }}>
                    {surplus > 0 ? '+' : ''}{surplus.toFixed(0)}
                  </strong>
                </span>
                <span style={{ ...small, minWidth: '4.5rem', color: n.down ? '#4ade80' : '#4b5563', fontWeight: 700 }}>
                  {n.down ? 'DARK' : ''}
                </span>
              </div>

              {/* Row two: strength, and the four squad buttons. Always mounted, always the same height. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginTop: '0.28rem', flexWrap: 'wrap' }}>
                <div style={{
                  width: 110, height: 8, background: '#161b22', borderRadius: 2,
                  overflow: 'hidden', flex: '0 0 auto',
                }}>
                  <div style={{
                    width: `${pct}%`, height: '100%',
                    background: n.down ? '#166534' : pct < 35 ? '#fbbf24' : '#3b82f6',
                    transition: 'width 200ms linear',
                  }} />
                </div>
                <span style={{ ...small, minWidth: '5.2rem', color: '#6b7280' }}>
                  {n.integrity.toFixed(0)}/{n.hardness.toFixed(0)}
                </span>
                {squads.map((s, q) => {
                  const on = s.node === i;
                  const pending = queue.some(x => x.squad === q && x.node === i);
                  return (
                    <button
                      key={s.name}
                      onClick={() => order(q, i)}
                      style={{
                        ...small, padding: '0.12rem 0.4rem', minWidth: '4.6rem',
                        border: `1px solid ${on ? 'var(--primary-color)' : pending ? '#fbbf24' : '#2b3444'}`,
                        background: on ? '#1d283a' : 'transparent',
                        color: on ? '#e2e8f0' : pending ? '#fbbf24' : '#6b7280',
                        borderRadius: 3, cursor: 'pointer',
                      }}
                      title={`${s.name}: ${s.bandwidth.toFixed(0)} Gbps`}
                    >
                      {s.name.slice(0, 1)}{on ? ` ${Math.round(s.ramp * 100)}%` : ` ${s.bandwidth.toFixed(0)}`}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', whiteSpace: 'nowrap' }}>
        {squads.map((s, q) => (
          <button
            key={s.name}
            onClick={() => order(q, -1)}
            style={{
              ...small, padding: '0.15rem 0.45rem',
              border: '1px solid #2b3444', background: 'transparent',
              color: s.node < 0 ? '#4b5563' : '#9aa4b2', borderRadius: 3, cursor: 'pointer',
            }}
            title={`Stand ${s.name} down — it stops attracting the pool`}
          >
            stand {s.name} down
          </button>
        ))}
        <button
          onClick={() => onSubmit({})}
          style={{ ...small, padding: '0.15rem 0.5rem', marginLeft: 'auto' }}
          title="Stop here — the grid is graded on what your orders actually did"
        >
          Break off
        </button>
      </div>
    </div>
  );
}
